import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Shield, Award, Lock } from 'lucide-react';
import heroBg from '../../assets/hero_bg.webp';

const trustItems = [
  { icon: Award,  label: 'Berpengalaman' },
  { icon: Shield, label: 'Profesional' },
  { icon: Lock,   label: 'Kerahasiaan Terjamin' },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.18 } },
};
const itemVariants = {
  hidden:  { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const handleScroll = (id) => {
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="beranda"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src={heroBg}
          alt="Kantor Hukum Pontianak Kalimantan Barat"
          className="w-full h-full object-cover object-center"
          fetchPriority="high"
          decoding="async"
        />
        {/* Multi-layer dark overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/95 via-navy-950/80 to-navy-900/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-transparent to-navy-950/40" />
      </div>

      {/* Animated gold orbs */}
      <motion.div
        animate={{ scale: [1, 1.15, 1], opacity: [0.07, 0.14, 0.07] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full"
        style={{ background: 'radial-gradient(circle, #d4950e 0%, transparent 70%)' }}
      />
      <motion.div
        animate={{ scale: [1, 1.2, 1], opacity: [0.05, 0.1, 0.05] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        className="absolute bottom-1/3 left-1/4 w-64 h-64 rounded-full"
        style={{ background: 'radial-gradient(circle, #d4950e 0%, transparent 70%)' }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <div className="max-w-3xl">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Badge */}
            <motion.div variants={itemVariants} className="mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-light text-gold-400 text-sm font-medium border border-gold-600/20">
                <span className="w-2 h-2 rounded-full bg-gold-400 animate-pulse" />
                Kantor Hukum Terpercaya di Pontianak
              </span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1
              variants={itemVariants}
              className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight text-shadow mb-6"
            >
              Solusi Hukum{' '}
              <span className="gold-text">Profesional</span>{' '}
              dan Terpercaya di{' '}
              <span className="gold-text">Pontianak</span>
            </motion.h1>

            {/* Subheadline */}
            <motion.p
              variants={itemVariants}
              className="text-navy-200 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl"
            >
              Pendampingan hukum untuk individu, bisnis, dan perusahaan dengan
              pendekatan profesional, cepat, dan strategis.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div variants={itemVariants} className="flex flex-wrap gap-4 mb-14">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleScroll('#kontak')}
                className="btn-primary text-base"
              >
                Konsultasi Sekarang
                <ArrowRight size={18} />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleScroll('#kontak')}
                className="btn-outline text-base"
              >
                <Phone size={18} />
                Hubungi Kami
              </motion.button>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              variants={itemVariants}
              className="flex flex-wrap gap-4"
            >
              {trustItems.map(({ icon: Icon, label }) => (
                <div
                  key={label}
                  className="flex items-center gap-2.5 glass-light px-4 py-2.5 rounded-xl"
                >
                  <div className="w-8 h-8 rounded-lg gold-gradient flex items-center justify-center flex-shrink-0">
                    <Icon size={15} className="text-navy-950" strokeWidth={2.5} />
                  </div>
                  <span className="text-sm font-medium text-navy-100">{label}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-navy-400 cursor-pointer"
        onClick={() => handleScroll('#tentang')}
      >
        <span className="text-xs tracking-widest uppercase">Gulir</span>
        <div className="w-px h-10 bg-gradient-to-b from-gold-600/60 to-transparent" />
      </motion.div>
    </section>
  );
}
